import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { hideAuthLoader, showAuthLoader, userSignInSuccess } from '@src/appRedux/actions/Auth';
import { resolveFirebaseAuthErrorMessage } from '@src/packages/utils/auth/firebaseAuthErrorMessage';
import type {
  MarkingConfirmCopy,
  MarkingPortalSession,
  MarkingPortalSessionError,
} from '@src/models/interfaces/markingPortal.interface';
import { MARKING_PORTAL_PIN_LENGTH } from '@src/models/interfaces/markingPortal.interface';
import {
  appendMarking,
  getEarlyMarkingWarningId,
  getMarkingConfirmCopy,
  withMarkingProgressForPortal,
} from '@src/models/mappers/markingPortalMarking';
import AuthService from '@src/services/AuthService';
import AttendanceEventService from '@src/services/AttendanceEventService';
import MarkingPortalService from '@src/services/MarkingPortalService';
import PrepareAttendanceSessionService from '@src/services/PrepareAttendanceSessionService';
import { DateUtil } from '@src/packages/utils/dates';

import { getMarkingPortalGeolocation } from '../markingPortal.geolocation';
import type { MarkingPortalGeolocationError } from '../markingPortal.geolocation';

import { getMarkingPortalToken } from '../markingPortal.utils';
import { signOutMarkingPortal } from '../markingPortal.auth';
import type { MarkingPortalConfirmModalPhase } from '../components/MarkingPortalConfirmModal';
import {
  clearMarkingPortalSession,
  computeSessionSecondsRemaining,
  createSessionExpiresAt,
  getMarkingPortalAuthUserId,
  persistMarkingPortalSession,
  readMarkingPortalSession,
} from '../markingPortal.storage';
import type { MarkingPortalStep } from '../markingPortal.types';

export interface MarkingPortalFormValues {
  email: string;
  password: string;
}

export const MARKING_PORTAL_TOTAL_STEPS = 2;
export const MARKING_PORTAL_SESSION_SECONDS = 120;
export const MARKING_PORTAL_SUCCESS_RESET_SECONDS = 10;

interface MarkingPortalSessionMeta {
  organizationId: string;
  attendanceRecordId?: string;
  employeeId: string;
  email: string;
}

const CHECKIN_ERROR_MESSAGE_IDS: Record<MarkingPortalSessionError, string> = {
  invalid_portal: 'markingPortal.checkIn.error.invalidPortal',
  portal_expired: 'markingPortal.checkIn.error.portalExpired',
  org_mismatch: 'markingPortal.checkIn.error.orgMismatch',
  unauthenticated: 'markingPortal.checkIn.error.unauthorized',
  invalid_argument: 'markingPortal.checkIn.error.loadFailed',
  no_locations: 'markingPortal.checkIn.error.noLocations',
  out_of_range: 'markingPortal.checkIn.error.outOfRange',
  missing_portal_token: 'markingPortal.checkIn.error.invalidPortal',
  location_unsupported: 'markingPortal.checkIn.error.locationUnsupported',
  location_denied: 'markingPortal.checkIn.error.locationDenied',
  location_unavailable: 'markingPortal.checkIn.error.locationUnavailable',
  location_timeout: 'markingPortal.checkIn.error.locationTimeout',
};

const GEOLOCATION_SESSION_ERRORS: Record<
  MarkingPortalGeolocationError,
  MarkingPortalSessionError
> = {
  unsupported: 'location_unsupported',
  denied: 'location_denied',
  unavailable: 'location_unavailable',
  timeout: 'location_timeout',
};

type AuthRootState = {
  settings?: { locale?: { locale?: string } };
};

export const useMarkingPortal = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const location = useLocation();
  const portalToken = getMarkingPortalToken(location.search);
  const locale = useSelector((state: AuthRootState) =>
    state.settings?.locale?.locale === 'es' ? 'es' : 'en',
  );

  const authServiceRef = useRef<AuthService | null>(null);
  if (authServiceRef.current === null) {
    authServiceRef.current = new AuthService();
  }

  const markingPortalServiceRef = useRef<MarkingPortalService | null>(null);
  if (markingPortalServiceRef.current === null) {
    markingPortalServiceRef.current = new MarkingPortalService();
  }

  const attendanceEventServiceRef = useRef<AttendanceEventService | null>(null);
  if (attendanceEventServiceRef.current === null) {
    attendanceEventServiceRef.current = new AttendanceEventService();
  }

  const prepareAttendanceSessionServiceRef = useRef<PrepareAttendanceSessionService | null>(null);
  if (prepareAttendanceSessionServiceRef.current === null) {
    prepareAttendanceSessionServiceRef.current = new PrepareAttendanceSessionService();
  }

  const sessionExpiresAtRef = useRef<number | null>(null);
  const hasRestoredSessionRef = useRef(false);
  const sessionExpiredHandledRef = useRef(false);

  const [form] = Form.useForm<MarkingPortalFormValues>();
  const [step, setStep] = useState<MarkingPortalStep>('identify');
  const [session, setSession] = useState<MarkingPortalSession | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [sessionSecondsRemaining, setSessionSecondsRemaining] = useState(0);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pinModalEmail, setPinModalEmail] = useState('');
  const [pinValue, setPinValue] = useState('');
  const [isSendingPin, setIsSendingPin] = useState(false);
  const [isSubmittingPin, setIsSubmittingPin] = useState(false);
  const [isResendingPin, setIsResendingPin] = useState(false);
  const [pinErrorMessageId, setPinErrorMessageId] = useState<string | undefined>();
  const [sessionMeta, setSessionMeta] = useState<MarkingPortalSessionMeta | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalPhase, setConfirmModalPhase] =
    useState<MarkingPortalConfirmModalPhase>('confirm');
  const [confirmModalCopy, setConfirmModalCopy] = useState<MarkingConfirmCopy | null>(null);
  const [pendingMarkingAt, setPendingMarkingAt] = useState<number | null>(null);
  const [successResetSecondsRemaining, setSuccessResetSecondsRemaining] = useState(
    MARKING_PORTAL_SUCCESS_RESET_SECONDS,
  );

  const currentStep = step === 'identify' ? 1 : MARKING_PORTAL_TOTAL_STEPS;

  const resetConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false);
    setConfirmModalPhase('confirm');
    setConfirmModalCopy(null);
    setPendingMarkingAt(null);
    setSuccessResetSecondsRemaining(MARKING_PORTAL_SUCCESS_RESET_SECONDS);
  }, []);

  const resetPinModal = useCallback(() => {
    setIsPinModalOpen(false);
    setPinModalEmail('');
    setPinValue('');
    setPinErrorMessageId(undefined);
  }, []);

  const resetToIdentify = useCallback(() => {
    sessionExpiresAtRef.current = null;
    clearMarkingPortalSession();
    resetPinModal();
    resetConfirmModal();
    form.resetFields();
    setSessionMeta(null);
    setStep('identify');
    setSession(null);
    setSessionSecondsRemaining(0);
  }, [form, resetConfirmModal, resetPinModal]);

  const persistActiveSession = useCallback(
    (
      email: string,
      authUserId: string,
      activeSession: MarkingPortalSession,
      meta?: { organizationId?: string; attendanceRecordId?: string },
    ) => {
      sessionExpiredHandledRef.current = false;
      const expiresAt = createSessionExpiresAt(MARKING_PORTAL_SESSION_SECONDS);
      sessionExpiresAtRef.current = expiresAt;
      persistMarkingPortalSession({
        portalToken: portalToken ?? '',
        authUserId,
        email,
        session: activeSession,
        expiresAt,
        organizationId: meta?.organizationId,
        attendanceRecordId: meta?.attendanceRecordId,
      });
      setSessionSecondsRemaining(computeSessionSecondsRemaining(expiresAt));
    },
    [portalToken],
  );

  const showErrorNotification = useCallback((message: string) => {
    notification.error({
      message,
      placement: 'topRight',
    });
  }, []);

  const handleCheckInError = useCallback(
    async (error?: MarkingPortalSessionError) => {
      const messageId =
        (error && CHECKIN_ERROR_MESSAGE_IDS[error]) || 'markingPortal.checkIn.error.loadFailed';
      showErrorNotification(intl.formatMessage({ id: messageId }));
      await signOutMarkingPortal(dispatch);
    },
    [dispatch, intl, showErrorNotification],
  );

  const completeLoginFlow = useCallback(
    async (email: string, uid: string): Promise<boolean> => {
      if (!portalToken) {
        await handleCheckInError('missing_portal_token');
        return false;
      }

      const geolocationResult = await getMarkingPortalGeolocation();
      if (geolocationResult.success === false) {
        await handleCheckInError(GEOLOCATION_SESSION_ERRORS[geolocationResult.error]);
        return false;
      }

      const checkInResult = await markingPortalServiceRef.current!.checkInSession({
        portalToken,
        lat: geolocationResult.position.lat,
        lng: geolocationResult.position.lng,
        accuracy: geolocationResult.position.accuracy,
        userId: uid,
      });

      if (!checkInResult.success || !checkInResult.session) {
        await handleCheckInError(checkInResult.error);
        return false;
      }

      persistActiveSession(email, uid, checkInResult.session, {
        organizationId: checkInResult.organizationId,
        attendanceRecordId: checkInResult.attendanceRecordId,
      });
      setSessionMeta({
        organizationId: checkInResult.organizationId,
        attendanceRecordId: checkInResult.attendanceRecordId,
        employeeId: uid,
        email,
      });
      dispatch(userSignInSuccess(uid));
      setSession(checkInResult.session);
      setStep('marking');
      return true;
    },
    [dispatch, handleCheckInError, persistActiveSession, portalToken],
  );

  /** Clears the portal UI/storage and tears down Firebase + Redux auth (shared totem). */
  const endPortalSession = useCallback(async () => {
    hasRestoredSessionRef.current = false;
    resetToIdentify();
    await signOutMarkingPortal(dispatch);
  }, [dispatch, resetToIdentify]);

  const handleSessionExpired = useCallback(async () => {
    if (sessionExpiredHandledRef.current) {
      return;
    }

    sessionExpiredHandledRef.current = true;
    await endPortalSession();
  }, [endPortalSession]);

  useEffect(() => {
    if (hasRestoredSessionRef.current) {
      return;
    }

    const authUserId = getMarkingPortalAuthUserId();
    if (!authUserId) {
      return;
    }

    const persisted = readMarkingPortalSession(portalToken, authUserId);
    hasRestoredSessionRef.current = true;

    if (!persisted) {
      return;
    }

    sessionExpiredHandledRef.current = false;
    sessionExpiresAtRef.current = persisted.expiresAt;
    setSession(persisted.session);
    if (persisted.organizationId) {
      setSessionMeta({
        organizationId: persisted.organizationId,
        attendanceRecordId: persisted.attendanceRecordId,
        employeeId: persisted.authUserId,
        email: persisted.email,
      });
    }
    setStep('marking');
    setSessionSecondsRemaining(computeSessionSecondsRemaining(persisted.expiresAt));
  }, [portalToken]);

  useEffect(() => {
    if (step !== 'marking' || sessionExpiresAtRef.current === null) {
      return undefined;
    }

    const tick = () => {
      const expiresAt = sessionExpiresAtRef.current;
      if (expiresAt === null) {
        return;
      }

      const remaining = computeSessionSecondsRemaining(expiresAt);
      setSessionSecondsRemaining(remaining);

      if (remaining <= 0) {
        handleSessionExpired();
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);

    return () => window.clearInterval(intervalId);
  }, [step, handleSessionExpired]);

  const onIdentify = useCallback(
    async (values: MarkingPortalFormValues) => {
      setIsSubmitting(true);
      dispatch(showAuthLoader());
      try {
        const result = await authServiceRef.current!.signInWithEmailPassword(
          values.email,
          values.password,
        );

        if (!result.success) {
          showErrorNotification(resolveFirebaseAuthErrorMessage(result.error, locale));
          return;
        }

        await completeLoginFlow(values.email, result.uid);
      } finally {
        setIsSubmitting(false);
        dispatch(hideAuthLoader());
      }
    },
    [completeLoginFlow, dispatch, locale, showErrorNotification],
  );

  const sendPinToEmail = useCallback(
    async (email: string): Promise<boolean> => {
      const result = await markingPortalServiceRef.current!.sendTemporaryPin(email);

      if (!result.success) {
        if (result.error === 'invalid_email') {
          form.setFields([
            {
              name: 'email',
              errors: [intl.formatMessage({ id: 'markingPortal.identify.email.required' })],
            },
          ]);
        } else {
          showErrorNotification(intl.formatMessage({ id: 'general.save.error.message' }));
        }
        return false;
      }

      return true;
    },
    [form, intl, showErrorNotification],
  );

  const onSendPin = useCallback(async () => {
    setPinErrorMessageId(undefined);

    let email = '';
    try {
      const values = await form.validateFields(['email']);
      email = values.email;
    } catch {
      return;
    }

    setIsSendingPin(true);
    try {
      const sent = await sendPinToEmail(email);
      if (!sent) {
        return;
      }

      setPinValue('');
      setPinModalEmail(email);
      setIsPinModalOpen(true);
    } finally {
      setIsSendingPin(false);
    }
  }, [form, sendPinToEmail]);

  const onSubmitPin = useCallback(async () => {
    if (!pinModalEmail || pinValue.length !== MARKING_PORTAL_PIN_LENGTH) {
      return;
    }

    setPinErrorMessageId(undefined);
    setIsSubmittingPin(true);
    dispatch(showAuthLoader());

    try {
      const result = await markingPortalServiceRef.current!.signInWithPin(pinModalEmail, pinValue);

      if (!result.success || !result.uid) {
        setPinErrorMessageId('markingPortal.pin.modal.invalidCode');
        return;
      }

      const advanced = await completeLoginFlow(pinModalEmail, result.uid);
      if (advanced) {
        resetPinModal();
      }
    } finally {
      setIsSubmittingPin(false);
      dispatch(hideAuthLoader());
    }
  }, [completeLoginFlow, dispatch, pinModalEmail, pinValue, resetPinModal]);

  const onResendPin = useCallback(async () => {
    if (!pinModalEmail) {
      return;
    }

    setPinErrorMessageId(undefined);
    setIsResendingPin(true);

    try {
      const sent = await sendPinToEmail(pinModalEmail);
      if (!sent) {
        return;
      }

      setPinValue('');
    } finally {
      setIsResendingPin(false);
    }
  }, [pinModalEmail, sendPinToEmail]);

  const onClosePinModal = useCallback(() => {
    resetPinModal();
  }, [resetPinModal]);

  const updatePersistedSession = useCallback(
    (activeSession: MarkingPortalSession) => {
      const authUserId = getMarkingPortalAuthUserId();
      const persisted = readMarkingPortalSession(portalToken, authUserId);
      if (!persisted) {
        setSession(activeSession);
        return;
      }

      persistMarkingPortalSession({
        ...persisted,
        session: activeSession,
      });
      setSession(activeSession);
    },
    [portalToken],
  );

  const formatPendingMarkingAt = useCallback(
    (at: number): string => {
      const timeZone = DateUtil.getDefaultTimeZone();
      const date = DateUtil.toDateString(at, DateUtil.DEFAULT_DT_DATE_FORMAT, timeZone);
      const time = DateUtil.toDateString(at, 'HH:mm:ss', timeZone);

      return intl.formatMessage({ id: 'markingPortal.marking.confirm.markingAt' }, { date, time });
    },
    [intl],
  );

  const onRegisterMarking = useCallback(() => {
    if (!session?.nextMarkingType) {
      return;
    }

    const copy = getMarkingConfirmCopy(session.nextMarkingType);
    if (!copy) {
      return;
    }

    setConfirmModalCopy(copy);
    setPendingMarkingAt(Date.now());
    setConfirmModalPhase('confirm');
    setSuccessResetSecondsRemaining(MARKING_PORTAL_SUCCESS_RESET_SECONDS);
    setIsConfirmModalOpen(true);
  }, [session]);

  const onCloseConfirmModal = useCallback(() => {
    if (confirmModalPhase === 'success') {
      return;
    }

    resetConfirmModal();
  }, [confirmModalPhase, resetConfirmModal]);

  const onConfirmRegisterMarking = useCallback(async () => {
    if (!session || !sessionMeta || !session.nextMarkingType || pendingMarkingAt === null) {
      return;
    }

    setIsRegistering(true);
    try {
      let attendanceRecordId = sessionMeta.attendanceRecordId;

      if (!attendanceRecordId) {
        const prepared = await prepareAttendanceSessionServiceRef.current!.prepareAdhocRecord({
          organizationId: sessionMeta.organizationId,
          shiftStartAtDevice: new Date(pendingMarkingAt).toISOString(),
        });
        attendanceRecordId = prepared.recordId;

        const updatedMeta: MarkingPortalSessionMeta = {
          ...sessionMeta,
          attendanceRecordId,
        };
        setSessionMeta(updatedMeta);

        const authUserId = getMarkingPortalAuthUserId();
        const persisted = readMarkingPortalSession(portalToken, authUserId);
        if (persisted) {
          persistMarkingPortalSession({
            ...persisted,
            attendanceRecordId,
          });
        }
      }

      await attendanceEventServiceRef.current!.registerEmployeeMarking({
        organizationId: sessionMeta.organizationId,
        attendanceRecordId,
        employeeId: sessionMeta.employeeId,
        attendanceType: session.nextMarkingType,
        actorFullname: session.displayName,
        timeZone: DateUtil.getDefaultTimeZone(),
        createdAtDevice: pendingMarkingAt,
      });

      const updatedSession = withMarkingProgressForPortal(
        session,
        appendMarking(session.completedMarkings, session.nextMarkingType, pendingMarkingAt),
        DateUtil.getDefaultTimeZone(),
      );
      updatePersistedSession(updatedSession);
      setConfirmModalPhase('success');
      setSuccessResetSecondsRemaining(MARKING_PORTAL_SUCCESS_RESET_SECONDS);
    } catch {
      showErrorNotification(intl.formatMessage({ id: 'markingPortal.marking.error' }));
    } finally {
      setIsRegistering(false);
    }
  }, [
    intl,
    pendingMarkingAt,
    portalToken,
    session,
    sessionMeta,
    showErrorNotification,
    updatePersistedSession,
  ]);

  const onBackToStartFromConfirm = useCallback(async () => {
    resetConfirmModal();
    await endPortalSession();
  }, [endPortalSession, resetConfirmModal]);

  useEffect(() => {
    if (!isConfirmModalOpen || confirmModalPhase !== 'success') {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setSuccessResetSecondsRemaining(previous => {
        if (previous <= 1) {
          return 0;
        }
        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [confirmModalPhase, isConfirmModalOpen]);

  useEffect(() => {
    if (!isConfirmModalOpen || confirmModalPhase !== 'success') {
      return;
    }

    if (successResetSecondsRemaining <= 0) {
      onBackToStartFromConfirm();
    }
  }, [
    confirmModalPhase,
    isConfirmModalOpen,
    onBackToStartFromConfirm,
    successResetSecondsRemaining,
  ]);

  /** Opens the same confirm → register flow used for other markings; next type is SHIFT_END. */
  const onFinishShift = useCallback(() => {
    onRegisterMarking();
  }, [onRegisterMarking]);

  const onLogout = useCallback(async () => {
    await endPortalSession();
  }, [endPortalSession]);

  const pendingMarkingAtDisplay =
    pendingMarkingAt !== null ? formatPendingMarkingAt(pendingMarkingAt) : '';

  const earlyMarkingWarningId =
    isConfirmModalOpen && confirmModalPhase === 'confirm'
      ? getEarlyMarkingWarningId(
          session?.nextMarkingType ?? null,
          pendingMarkingAt,
          session?.scheduledTimes,
        )
      : null;

  return {
    form,
    step,
    session,
    currentStep,
    isSubmitting,
    isRegistering,
    sessionSecondsRemaining,
    isPinModalOpen,
    pinModalEmail,
    pinValue,
    isSendingPin,
    isSubmittingPin,
    isResendingPin,
    pinErrorMessageId,
    isConfirmModalOpen,
    confirmModalPhase,
    confirmModalCopy,
    pendingMarkingAtDisplay,
    earlyMarkingWarningId,
    successResetSecondsRemaining,
    onIdentify,
    onSendPin,
    onSubmitPin,
    onResendPin,
    onClosePinModal,
    onPinChange: setPinValue,
    onRegisterMarking,
    onConfirmRegisterMarking,
    onCloseConfirmModal,
    onBackToStartFromConfirm,
    onFinishShift,
    onLogout,
  };
};
